import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ticketAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Plus,
  Search,
  Filter,
  Bug,
  Lightbulb,
  RefreshCw,
  Clock,
  User,
  Building,
  Loader2,
  ChevronRight
} from 'lucide-react';

const STATUSES = ['all', 'open', 'wip', 'resolved', 'closed'];
const TYPES = ['all', 'bug', 'requirement', 'change_request'];
const DEPARTMENTS = ['all', 'Finance', 'Sales', 'IT', 'HR', 'Management'];

const Tickets = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: 'all',
    ticket_type: 'all',
    department: 'all',
    search: ''
  });
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    fetchTickets();
  }, [filters, activeTab]);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status !== 'all') params.status = filters.status;
      if (filters.ticket_type !== 'all') params.ticket_type = filters.ticket_type;
      if (filters.department !== 'all') params.department = filters.department;
      if (activeTab === 'my') params.created_by_me = true;
      if (activeTab === 'assigned') params.assigned_to_me = true;

      const response = await ticketAPI.getTickets(params);
      let filteredTickets = response.data;

      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        filteredTickets = filteredTickets.filter(t =>
          t.title.toLowerCase().includes(searchLower) ||
          t.description.toLowerCase().includes(searchLower) ||
          (t.ticket_number || '').toLowerCase().includes(searchLower) ||
          t.system.toLowerCase().includes(searchLower)
        );
      }

      setTickets(filteredTickets);
    } catch (error) {
      console.error('Failed to fetch tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'bug': return <Bug className="h-4 w-4" />;
      case 'requirement': return <Lightbulb className="h-4 w-4" />;
      case 'change_request': return <RefreshCw className="h-4 w-4" />;
      default: return null;
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'open': return 'status-open';
      case 'wip': return 'status-wip';
      case 'resolved': return 'status-resolved';
      case 'closed': return 'status-closed';
      default: return '';
    }
  };

  const getPriorityClass = (priority) => {
    switch (priority) {
      case 'low': return 'priority-low';
      case 'medium': return 'priority-medium';
      case 'high': return 'priority-high';
      case 'critical': return 'priority-critical';
      default: return '';
    }
  };

  return (
    <div className="space-y-6" data-testid="tickets-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight">
            Tickets
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage support tickets, requirements and change requests
          </p>
        </div>
        <Button
          onClick={() => navigate('/tickets/new')}
          className="gap-2 rounded-sm"
          data-testid="new-ticket-btn"
        >
          <Plus className="h-4 w-4" />
          New Ticket
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="rounded-sm">
          <TabsTrigger value="all" className="rounded-sm" data-testid="tab-all">All Tickets</TabsTrigger>
          <TabsTrigger value="my" className="rounded-sm" data-testid="tab-my">My Tickets</TabsTrigger>
          {['support', 'pm', 'developer', 'admin'].includes(user?.role) && (
            <TabsTrigger value="assigned" className="rounded-sm" data-testid="tab-assigned">Assigned to Me</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value={activeTab} className="mt-6">
          {/* Filters */}
          <div className="flex flex-wrap gap-4 mb-6">
            <div className="relative flex-1 min-w-0 sm:min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search tickets..."
                value={filters.search}
                onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
                className="pl-10 rounded-sm"
                data-testid="search-input"
              />
            </div>
            <Select
              value={filters.status}
              onValueChange={(v) => setFilters(f => ({ ...f, status: v }))}
            >
              <SelectTrigger className="w-36 rounded-sm" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                {STATUSES.map((s) => (
                  <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={filters.ticket_type}
              onValueChange={(v) => setFilters(f => ({ ...f, ticket_type: v }))}
            >
              <SelectTrigger className="w-40 rounded-sm" data-testid="type-filter">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                {TYPES.map((t) => (
                  <SelectItem key={t} value={t} className="capitalize">
                    {t === 'all' ? 'All Types' : t.replace('_', ' ')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={filters.department}
              onValueChange={(v) => setFilters(f => ({ ...f, department: v }))}
            >
              <SelectTrigger className="w-40 rounded-sm" data-testid="dept-filter">
                <SelectValue placeholder="Department" />
              </SelectTrigger>
              <SelectContent>
                {DEPARTMENTS.map((d) => (
                  <SelectItem key={d} value={d}>{d === 'all' ? 'All Departments' : d}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Tickets List */}
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : tickets.length === 0 ? (
            <div className="border border-dashed rounded-sm p-12 text-center">
              <p className="text-muted-foreground">No tickets found</p>
              <Button
                variant="link"
                onClick={() => navigate('/tickets/new')}
                className="mt-2"
              >
                Create your first ticket
              </Button>
            </div>
          ) : (
            <div className="border rounded-sm divide-y divide-border">
              {tickets.map((ticket) => (
                <Link
                  key={ticket.id}
                  to={`/tickets/${ticket.id}`}
                  className="block p-4 hover:bg-muted/50 transition-colors ticket-card"
                  data-testid={`ticket-${ticket.id}`}
                >
                  <div className="flex items-start gap-4">
                    {/* Type Icon */}
                    <div className={`p-2 rounded-sm ${
                      ticket.ticket_type === 'bug' ? 'bg-red-100 text-red-600' :
                      ticket.ticket_type === 'requirement' ? 'bg-blue-100 text-blue-600' :
                      'bg-purple-100 text-purple-600'
                    }`}>
                      {getTypeIcon(ticket.ticket_type)}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <span className="font-mono text-xs text-primary font-medium" data-testid={`ticket-number-${ticket.id}`}>
                            {ticket.ticket_number}
                          </span>
                          <h3 className="font-semibold text-foreground">{ticket.title}</h3>
                          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                            {ticket.description}
                          </p>
                        </div>
                        <ChevronRight className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                      </div>

                      {/* Meta */}
                      <div className="flex flex-wrap items-center gap-3 mt-3">
                        <Badge className={getStatusClass(ticket.status)}>
                          {ticket.status.toUpperCase()}
                        </Badge>
                        <Badge className={getPriorityClass(ticket.priority)}>
                          {ticket.priority}
                        </Badge>
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Building className="h-3 w-3" />
                          {ticket.department}
                        </span>
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <User className="h-3 w-3" />
                          {ticket.created_by.name}
                        </span>
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {new Date(ticket.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Tickets;
